import { Box, FormControl, FormControlProps, InputLabel } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import Button from '@ui/Button';
import Typography from '@ui/Typography';

const useStyles = makeStyles()(theme => ({
  label: {
    '& + div': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    visibility: 'hidden',
    display: 'none',
  },
}));

export interface FileInputProps
  extends Omit<
    React.HTMLProps<HTMLInputElement>,
    'multiple' | 'value' | 'onChange'
  > {
  id: string;
  label?: string;
  fullWidth?: FormControlProps['fullWidth'];
  value: File | null;
  onChange: (selectedFile: File | null) => void;
}

/**
 * @description The FileInput component is a wrapper around the native file input element that
 * allows the user to select a file, see the filename of the selected file, and deselect the file.
 * This component currently does not support 'multiple' file selection.
 */
const FileInput = (props: FileInputProps) => {
  const { classes } = useStyles();
  const { id, label, fullWidth = true, disabled, ...inputProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  // NOTE: this component controls a native "file" input internally. We only expose the selected
  // "File" to the parent component during the onChange event.
  const [nativeFileInput, setNativeFileInput] = useState<string>('');

  const handleActionButtonClick = useCallback(() => {
    if (!inputProps.value) {
      if (inputRef.current) inputRef.current.click();
    } else {
      setNativeFileInput('');
      inputProps.onChange(null);
    }
  }, [inputProps]);

  const handleNativeInputChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      event => {
        const target = event.target as HTMLInputElement;
        if (target.validity.valid) {
          setNativeFileInput(target.value);
          inputProps.onChange(target.files ? target.files[0] : null);
        } else {
          setNativeFileInput('');
          inputProps.onChange(null);
        }
      },
      [inputProps]
    );

  useEffect(() => {
    // Ensuring this is a controlled component. If the parent's state becomes null, we reset our
    // local state to reflect that.
    if (inputProps.value === null) {
      setNativeFileInput('');
    }
  }, [inputProps.value]);

  return (
    <FormControl fullWidth={fullWidth}>
      {label && (
        <InputLabel className={classes.label} shrink htmlFor={id}>
          {label}
        </InputLabel>
      )}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="pMd" noWrap color="tertiary">
          {inputProps.value ? inputProps.value.name : `No file selected`}
        </Typography>
        <Button
          variant="secondary"
          size="small"
          disabled={disabled}
          onClick={handleActionButtonClick}
        >
          {inputProps.value ? `Remove` : `Select`}
        </Button>
        {/* Hidden input element needed for programmatic native file select */}
        <input
          {...inputProps}
          value={nativeFileInput}
          disabled={disabled}
          onChange={handleNativeInputChange}
          ref={inputRef}
          className={classes.input}
          type="file"
        />
      </Box>
    </FormControl>
  );
};

export default FileInput;
